import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, ArrowLeft } from '@phosphor-icons/react';

/**
 * /logos — one page with every logo for all three products.
 * Each card shows the logo and offers direct one-click PNG/SVG downloads.
 * No zip files — the `download` attribute saves straight to the computer.
 */

const CHECKER = {
  backgroundImage:
    'linear-gradient(45deg, #e2e8f0 25%, transparent 25%), ' +
    'linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), ' +
    'linear-gradient(45deg, transparent 75%, #e2e8f0 75%), ' +
    'linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)',
  backgroundSize: '14px 14px',
  backgroundPosition: '0 0, 0 7px, 7px -7px, -7px 0px',
  backgroundColor: '#f8fafc',
};

function variantsFor(slug) {
  return [
    {
      label: 'Horizontal — Dark',
      use: 'Dark headers, YouTube banners, social covers',
      preview: `/brand/${slug}-logo-horizontal-800.png`,
      tile: 'checker',
      files: [
        { tag: 'PNG 400', href: `/brand/${slug}-logo-horizontal-400.png` },
        { tag: 'PNG 800', href: `/brand/${slug}-logo-horizontal-800.png` },
        { tag: 'PNG 1600', href: `/brand/${slug}-logo-horizontal-1600.png` },
        { tag: 'SVG', href: `/brand/${slug}-logo-horizontal.svg` },
      ],
    },
    {
      label: 'Horizontal — Light',
      use: 'Light backgrounds, print, invoices',
      preview: `/brand/${slug}-logo-horizontal-light-800.png`,
      tile: 'checker',
      files: [
        { tag: 'PNG 400', href: `/brand/${slug}-logo-horizontal-light-400.png` },
        { tag: 'PNG 800', href: `/brand/${slug}-logo-horizontal-light-800.png` },
        { tag: 'PNG 1600', href: `/brand/${slug}-logo-horizontal-light-1600.png` },
        { tag: 'SVG', href: `/brand/${slug}-logo-horizontal-light.svg` },
      ],
    },
    {
      label: 'Stacked — Square',
      use: 'Avatars, app icons, YouTube profile',
      preview: `/brand/${slug}-logo-stacked-512.png`,
      tile: 'checker',
      files: [
        { tag: 'PNG 128', href: `/brand/${slug}-logo-stacked-128.png` },
        { tag: 'PNG 256', href: `/brand/${slug}-logo-stacked-256.png` },
        { tag: 'PNG 512', href: `/brand/${slug}-logo-stacked-512.png` },
        { tag: 'SVG', href: `/brand/${slug}-logo-stacked.svg` },
      ],
    },
    {
      label: 'Mono — White',
      use: 'Watermarks, single-color on dark',
      preview: `/brand/${slug}-logo-mono-light-512.png`,
      tile: 'dark',
      files: [
        { tag: 'PNG 512', href: `/brand/${slug}-logo-mono-light-512.png` },
        { tag: 'SVG', href: `/brand/${slug}-logo-mono-light.svg` },
      ],
    },
    {
      label: 'Mono — Dark',
      use: 'Single-color on light backgrounds',
      preview: `/brand/${slug}-logo-mono-dark-512.png`,
      tile: 'checker',
      files: [
        { tag: 'PNG 512', href: `/brand/${slug}-logo-mono-dark-512.png` },
        { tag: 'SVG', href: `/brand/${slug}-logo-mono-dark.svg` },
      ],
    },
  ];
}

const PRODUCTS = [
  {
    name: 'VideoCrunch',
    slug: 'videocrunch',
    tagline: 'AI tutorial videos that are never wrong',
    headerClass: 'bg-gradient-to-r from-[#0a0e27] to-[#101636]',
    accent: '#F39C12',
    variants: variantsFor('videocrunch'),
  },
  {
    name: 'SalesCrunch CRM',
    slug: 'salescrunchcrm',
    tagline: 'The fast, familiar CRM',
    headerClass: 'bg-gradient-to-r from-[#0B5394] to-[#073763]',
    accent: '#0B5394',
    variants: variantsFor('salescrunchcrm'),
  },
  {
    name: 'MigrationCrunch',
    slug: 'migrationcrunch',
    tagline: 'CRM migration without the agency bill',
    headerClass: 'bg-gradient-to-r from-[#0F0F1A] to-[#1a1a2e]',
    accent: '#F39C12',
    variants: variantsFor('migrationcrunch'),
  },
];

function LogoCard({ variant, slug }) {
  const tileStyle = variant.tile === 'dark' ? { background: '#0F0F1A' } : CHECKER;
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm flex flex-col"
      data-testid={`logos-card-${slug}-${variant.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
    >
      <div className="flex items-center justify-center p-6 min-h-[150px] border-b border-slate-200" style={tileStyle}>
        <img src={variant.preview} alt={`${slug} ${variant.label}`} className="max-w-full max-h-[130px] object-contain" loading="lazy" />
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-black text-sm text-slate-900">{variant.label}</div>
        <div className="text-[11px] text-slate-500 mt-0.5 mb-3">{variant.use}</div>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {variant.files.map((f) => (
            <a
              key={f.tag}
              href={f.href}
              download
              className="inline-flex items-center gap-1 bg-slate-100 hover:bg-[#F39C12] hover:text-black text-slate-700 border border-slate-200 font-bold rounded-md px-2.5 py-1.5 text-[11px] transition-colors"
              data-testid={`logos-dl-${slug}-${f.href.split('/').pop().replace(/\./g, '-')}`}
            >
              <Download size={12} weight="bold" /> {f.tag}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function LogosPage() {
  useEffect(() => {
    document.title = 'Logo Library — SalesCrunch, MigrationCrunch & VideoCrunch';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="logos-page">
      <nav className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-[#0B5394]" data-testid="logos-back-home">
            <ArrowLeft size={16} weight="bold" /> Home
          </Link>
          <div className="flex items-center gap-2">
            {PRODUCTS.map((p) => (
              <a key={p.slug} href={`#${p.slug}-logos`} className="text-xs font-bold text-slate-500 hover:text-[#0B5394] px-2 py-1" data-testid={`logos-nav-${p.slug}`}>
                {p.name}
              </a>
            ))}
          </div>
        </div>
      </nav>

      <header className="px-6 pt-14 pb-10 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="text-[10px] uppercase tracking-widest font-black text-[#F39C12] mb-2">Brand assets</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-3">Logo Library</h1>
          <p className="text-base text-slate-600 max-w-2xl">
            Every logo for all three products, in every size. Click a button and it saves
            straight to your computer — no zip files, no hunting through folders.
          </p>
        </div>
      </header>

      <main className="px-6 py-12">
        <div className="max-w-6xl mx-auto space-y-14">
          {PRODUCTS.map((p) => (
            <section key={p.slug} id={`${p.slug}-logos`} className="scroll-mt-24" data-testid={`logos-section-${p.slug}`}>
              <div className={`${p.headerClass} text-white rounded-t-lg px-6 py-5 flex items-center justify-between`}>
                <div>
                  <h2 className="text-lg font-black">{p.name}</h2>
                  <div className="text-xs text-white/60">{p.tagline}</div>
                </div>
                <img src={`/brand/${p.slug}-logo-stacked-128.png`} alt={p.name} className="h-12 w-12 rounded" />
              </div>
              <div className="bg-white border border-t-0 border-slate-200 rounded-b-lg p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {p.variants.map((v) => (
                  <LogoCard key={v.label} variant={v} slug={p.slug} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>

      <footer className="px-6 py-10 text-center text-xs text-slate-400 border-t border-slate-200 bg-white">
        Need banners, email swipes, or the full media kit?{' '}
        <Link to="/affiliates" className="text-[#0B5394] font-bold hover:underline" data-testid="logos-footer-affiliates">
          Visit the Partner Hub →
        </Link>
      </footer>
    </div>
  );
}
